import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Course, CourseProgress } from '../types';
import { coursesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const CourseLanding: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState<Course | null>(null);
  const [progress, setProgress] = useState<CourseProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEnrolled, setIsEnrolled] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      
      try {
        const courseData = await coursesAPI.getCourse(parseInt(id));
        setCourse(courseData);
        
        // If user is logged in, check enrollment and get progress
        if (user) {
          try {
            const progressData = await coursesAPI.getCourseProgress(parseInt(id));
            setProgress(progressData);
            setIsEnrolled(true);
          } catch (error: any) {
            // User might not be enrolled yet
            if (error.response?.status === 403) {
              setIsEnrolled(false);
            } else {
              console.error('Error fetching progress:', error);
              setIsEnrolled(false);
            }
          }
        }
      } catch (error: any) {
        setError('Failed to load course details');
        console.error('Error fetching course:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, user]);

  const handleEnroll = async () => {
    if (!course || !user) return;
    
    try {
      await coursesAPI.enrollInCourse(course.id);
      setIsEnrolled(true);
      
      // Get initial progress
      const progressData = await coursesAPI.getCourseProgress(course.id);
      setProgress(progressData);
    } catch (error: any) {
      console.error('Failed to enroll:', error);
      
      // Check if already enrolled
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('Already enrolled')) {
        setIsEnrolled(true);
        // Get progress for already enrolled user
        try {
          const progressData = await coursesAPI.getCourseProgress(course.id);
          setProgress(progressData);
        } catch (progressError) {
          console.error('Failed to get progress:', progressError);
        }
      } else {
        alert('Failed to enroll in course');
      }
    }
  };

  const handleStartCourse = () => {
    if (!course) return;
    
    if (progress?.next_lesson) {
      // Resume from next lesson
      navigate(`/courses/${course.id}/lessons/${progress.next_lesson}`);
    } else if (course.lessons.length > 0) {
      // Start from first lesson
      const firstLesson = course.lessons.sort((a, b) => a.order - b.order)[0];
      navigate(`/courses/${course.id}/lessons/${firstLesson.id}`);
    }
  };

  const getFileIcon = (fileType: string) => {
    switch (fileType.toLowerCase()) {
      case 'pdf':
        return '📄';
      case 'doc':
      case 'docx':
        return '📝';
      case 'mp3':
      case 'wav':
        return '🎵';
      case 'mp4':
      case 'webm':
      case 'ogg':
        return '🎬';
      default:
        return '📁';
    }
  };

  const getTotalDuration = () => {
    // Calculate estimated total duration based on content
    if (!course) return '0 min';
    
    let totalMinutes = 0;
    course.lessons.forEach(lesson => {
      // Estimate: 5 min per lesson + 2 min per file
      totalMinutes += 5 + ((lesson.files?.length || 0) * 2);
    });
    
    if (totalMinutes < 60) {
      return `${totalMinutes} min`;
    } else {
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      return `${hours}h ${minutes}m`;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Course Not Found</h1>
          <p className="text-gray-600 mb-8">{error || 'The course you are looking for does not exist.'}</p>
          <Link
            to="/"
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold"
          >
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const sortedLessons = [...course.lessons].sort((a, b) => a.order - b.order);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-700 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h1 className="text-4xl lg:text-5xl font-bold mb-6">{course.title}</h1>
              <p className="text-xl mb-8 text-blue-100">{course.description}</p>
              
              <div className="flex items-center space-x-8 mb-8 text-blue-100">
                <span className="flex items-center">
                  👨‍🏫 {course.instructor?.full_name}
                </span>
                <span className="flex items-center">
                  📚 {course.lessons.length} lessons
                </span>
                <span className="flex items-center">
                  ⏱️ {getTotalDuration()}
                </span>
              </div>

              {user ? (
                isEnrolled ? (
                  <div className="space-y-4">
                    <button
                      onClick={handleStartCourse}
                      className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors"
                    >
                      {progress?.completed_lessons ? 'Continue Learning' : 'Start Learning'}
                    </button>
                    {progress && (
                      <div className="bg-blue-800 bg-opacity-50 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm">Progress</span>
                          <span className="text-sm">{progress.progress_percentage.toFixed(1)}%</span>
                        </div>
                        <div className="bg-blue-900 rounded-full h-2">
                          <div 
                            className="bg-white h-2 rounded-full transition-all duration-300" 
                            style={{ width: `${progress.progress_percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={handleEnroll}
                    className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors"
                  >
                    Enroll Now - ${course.price}
                  </button>
                )
              ) : (
                <Link
                  to="/login"
                  className="inline-block bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors"
                >
                  Login to Enroll - ${course.price}
                </Link>
              )}
            </div>

            <div className="hidden lg:block">
              <div className="bg-white bg-opacity-10 backdrop-blur rounded-2xl p-8">
                <h3 className="text-xl font-semibold mb-4">What you'll learn</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <span className="text-green-400 mr-3">✓</span>
                    Master the fundamentals and advanced concepts
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-400 mr-3">✓</span>
                    Build real-world projects and applications
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-400 mr-3">✓</span>
                    Get hands-on experience with industry tools
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-400 mr-3">✓</span>
                    Gain practical skills for your career
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Course Structure */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Course Curriculum</h2>
            
            <div className="space-y-4">
              {sortedLessons.map((lesson, index) => {
                const isCompleted = progress ? progress.completed_lesson_ids.includes(lesson.id) : false;
                const isAccessible = isEnrolled;
                
                return (
                  <div key={lesson.id} className={`bg-white rounded-lg shadow-md overflow-hidden ${
                    isAccessible ? 'hover:shadow-lg transition-shadow' : ''
                  }`}>
                    <div className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center mb-3">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium mr-4 ${
                              isCompleted 
                                ? 'bg-green-100 text-green-800' 
                                : isAccessible
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {isCompleted ? '✓' : isAccessible ? lesson.order : '🔒'} 
                              {isAccessible ? ` Lesson ${lesson.order}` : ' Locked'}
                            </span>
                            <h3 className="text-lg font-semibold text-gray-900">
                              {lesson.title}
                            </h3>
                          </div>
                          
                          {lesson.description && (
                            <p className="text-gray-600 mb-4">{lesson.description}</p>
                          )}
                          
                          {lesson.files && lesson.files.length > 0 && (
                            <div className="flex flex-wrap gap-3">
                              {lesson.files.map((file, idx) => (
                                <span key={idx} className="inline-flex items-center px-2 py-1 bg-gray-100 rounded text-xs">
                                  {getFileIcon(file.file_type)}
                                  <span className="ml-1">{file.file_type.toUpperCase()}</span>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        {isAccessible && (
                          <Link
                            to={`/courses/${course.id}/lessons/${lesson.id}`}
                            className="ml-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
                          >
                            {isCompleted ? 'Review' : 'Start'}
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Course Stats */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Course Details</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Lessons</span>
                  <span className="font-medium">{course.lessons.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Duration</span>
                  <span className="font-medium">{getTotalDuration()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Level</span>
                  <span className="font-medium">Beginner to Advanced</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Price</span>
                  <span className="font-medium text-green-600">${course.price}</span>
                </div>
              </div>
            </div>

            {/* Instructor */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Instructor</h3>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-lg">
                  {course.instructor?.full_name.charAt(0)}
                </div>
                <div className="ml-4">
                  <p className="font-medium text-gray-900">{course.instructor?.full_name}</p>
                  <p className="text-sm text-gray-600">Course Instructor</p>
                </div>
              </div>
            </div>

            {/* Action Button */}
            <div className="bg-white rounded-lg shadow-md p-6">
              {user ? (
                isEnrolled ? (
                  <button
                    onClick={handleStartCourse}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold"
                  >
                    {progress?.completed_lessons ? 'Continue Course' : 'Start Course'}
                  </button>
                ) : (
                  <button
                    onClick={handleEnroll}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold"
                  >
                    Enroll Now
                  </button>
                )
              ) : (
                <Link
                  to="/login"
                  className="block w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold text-center"
                >
                  Login to Enroll
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseLanding; 